import {produce} from 'immer';
import isEqual from 'react-fast-compare';

import {assert} from 'Asserts';
import {ProcessFlowchartNodeDetailsType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {buildFieldDefinitionsFromSchemaFields} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/nodes/nodeUtils';
import {FlowchartEditorMode} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/ProcessFlowchartEditorModeConstants';
import {PLUGIN_REGISTRY_INSTANCE} from 'processes/graph-builder-processes/platform/PluginRegistry';

import {ProcessTaskSchemaWithoutFlowchartConfigGraphQL} from '../../../../../graphql-fragments/processes/schemas/__generated/ProcessTaskSchemaWithoutFlowchartConfigGraphQL';
import {
  FieldDefinitionGraphQL,
  TaskDefinitionVersionSpecWithTaskSchemaFlowchart,
  TaskSchemaNodeSectionType,
} from './processFlowchartConfigEditorConstants';
import {convertStaticFlowchartToSpecFlowchartWithTaskSchema} from './processFlowchartConfigEditorNetworkUtils';
import {
  isStaticTaskSchemaNodeData,
  TaskDefinitionVersionSpecWithTaskSchema,
  TaskSchemaStaticNodeData,
} from './processFlowchartConfigEditorNodeTypes';
import {FlowchartNodeChange, FlowchartNodeChangeType} from './processFlowchartConfigEditorUtils';
import {FlowchartConfigWithMetadata} from './redux/flowchartConfigAutosaveManagerCallbacks';

export default function computeTaskDefinitionVersionUpdateChanges(
  flowchartDraftState: FlowchartConfigWithMetadata,
  selectedNodeId: string,
  updater: (
    currentFlowchart: TaskDefinitionVersionSpecWithTaskSchemaFlowchart
  ) => TaskDefinitionVersionSpecWithTaskSchema<unknown>,
  shouldIncrementNodeNames: boolean,
  editorMode: FlowchartEditorMode
): {nodeChanges: Array<FlowchartNodeChange>} | Record<string, never> {
  const selectedNodeData = flowchartDraftState.staticFlowchart.nodesStaticData.find(
    (node) => node.id === selectedNodeId
  );
  if (selectedNodeData == null) {
    return {};
  }
  assert(isStaticTaskSchemaNodeData(selectedNodeData), 'Expected node to be a static task node');

  const currentFlowchart = convertStaticFlowchartToSpecFlowchartWithTaskSchema(
    flowchartDraftState.staticFlowchart
  );

  let newTaskDefinitionVersionSpec = updater(currentFlowchart);
  const adapter = PLUGIN_REGISTRY_INSTANCE.getTaskSchemaNodeAdapter(
    selectedNodeData.taskDefinitionVersionSpec.newProcessTaskDefinitionVersion.taskSchema.executionType
  );

  // Find all nodes that share the same task definition version and update them all.
  // For singleton schemas (like procedure unit operations), all nodes share the same taskSchemaId
  // but have unique task definition versions, so we must match by the definition version ID.
  const taskDefinitionVersionClientId = selectedNodeData.taskDefinitionVersionSpec.clientId;
  const nodesToUpdate = flowchartDraftState.staticFlowchart.nodesStaticData.filter(
    (node): node is TaskSchemaStaticNodeData =>
      isStaticTaskSchemaNodeData(node) &&
      node.taskDefinitionVersionSpec.clientId === taskDefinitionVersionClientId
  );

  newTaskDefinitionVersionSpec =
    adapter.updateTaskDefinitionVersionWithApplicationData?.(newTaskDefinitionVersionSpec) ??
    newTaskDefinitionVersionSpec;

  const nodeIdsBeingUpdated = new Set(nodesToUpdate.map((node) => node.id));
  const existingNodeNames = shouldIncrementNodeNames
    ? flowchartDraftState.staticFlowchart.nodesStaticData
        .filter((node) => !nodeIdsBeingUpdated.has(node.id))
        .map((node) => node.nodeName)
    : undefined;

  const nodeChanges: Array<FlowchartNodeChange> = nodesToUpdate.map((nodeToUpdate) => {
    const newSections = adapter.createSections(newTaskDefinitionVersionSpec, nodeToUpdate.nodeDetailsType);

    const oldMapFromStart = nodeToUpdate.sections.find(
      (section) => section.sectionType === TaskSchemaNodeSectionType.MAP_FROM_START
    );
    const newMapFromStart = newSections.find(
      (section) => section.sectionType === TaskSchemaNodeSectionType.MAP_FROM_START
    );
    if (oldMapFromStart && newMapFromStart) {
      newMapFromStart.sectionRows = oldMapFromStart.sectionRows;
    }

    const nodeName = adapter.getNodeName(
      newTaskDefinitionVersionSpec,
      editorMode,
      nodeToUpdate.nodeDetailsType === ProcessFlowchartNodeDetailsType.OUTPUT_NODE_DETAILS,
      existingNodeNames
    );

    existingNodeNames?.push(nodeName);

    return {
      type: FlowchartNodeChangeType.REPLACE as const,
      id: nodeToUpdate.id,
      data: {
        ...nodeToUpdate,
        taskDefinitionVersionSpec: newTaskDefinitionVersionSpec,
        nodeName,
        sections: newSections,
      },
    };
  });

  return {nodeChanges};
}

/**
 * Merges fresh field definitions (from live schema) with existing ones (from the node's stored PTDV),
 * preserving the existing fieldDefinition object for fields that already exist. This keeps the
 * original __typename (e.g. 'AaPartLinkFieldDefinitionNode') so getChangedFieldTypeClientIds
 * doesn't treat every refresh as a type change and wipe custom lookup mappings.
 * New fields (added to the schema) get the fresh fieldDefinition from `next`.
 */
function mergeFieldDefinitions(
  existing: Array<FieldDefinitionGraphQL> | null | undefined,
  next: Array<FieldDefinitionGraphQL>
): Array<FieldDefinitionGraphQL> {
  const existingByClientId = new Map((existing ?? []).map((f) => [f.fieldClientIdentifier, f]));
  return next.map((updated) => {
    const old = existingByClientId.get(updated.fieldClientIdentifier);
    if (old == null) {
      return updated;
    }
    // __typename is preserved from old so getChangedFieldTypeClientIds doesn't see a spurious change.
    return {
      ...updated,
      fieldDefinition: {
        ...old.fieldDefinition,
        ...updated.fieldDefinition,
        __typename: old.fieldDefinition.__typename,
      },
    } as FieldDefinitionGraphQL;
  });
}

/**
 * For each node whose task schema fields differ from the corresponding schema in `liveSchemas`,
 * generates REPLACE node changes that refresh sections and field definitions without marking
 * the flowchart dirty. Used to keep nodes in sync when a task schema is updated externally.
 */
export function computeStaleSchemaSectionChanges(
  flowchartDraftState: FlowchartConfigWithMetadata,
  liveSchemas: Array<ProcessTaskSchemaWithoutFlowchartConfigGraphQL>,
  shouldIncrementNodeNames: boolean,
  editorMode: FlowchartEditorMode
): Array<FlowchartNodeChange> {
  const liveSchemaById = new Map(liveSchemas.map((s) => [s.id, s]));
  const allNodeChanges: Array<FlowchartNodeChange> = [];
  const processedClientIds = new Set<string>();

  for (const node of flowchartDraftState.staticFlowchart.nodesStaticData) {
    if (!isStaticTaskSchemaNodeData(node)) {
      continue;
    }
    const updatedSchema = liveSchemaById.get(node.taskSchemaId);
    if (updatedSchema == null) {
      continue;
    }
    const nodeFields = node.taskDefinitionVersionSpec.newProcessTaskDefinitionVersion.taskSchema.fields;
    const nodeOutputFields =
      node.taskDefinitionVersionSpec.newProcessTaskDefinitionVersion.taskSchema.outputSchema?.fields;
    if (
      isEqual(nodeFields, updatedSchema.fields) &&
      isEqual(nodeOutputFields ?? null, updatedSchema.outputSchema?.fields ?? null)
    ) {
      continue;
    }
    if (processedClientIds.has(node.taskDefinitionVersionSpec.clientId)) {
      continue;
    }
    processedClientIds.add(node.taskDefinitionVersionSpec.clientId);

    const result = computeTaskDefinitionVersionUpdateChanges(
      flowchartDraftState,
      node.id,
      (currentFlowchart) =>
        produce(currentFlowchart.nodes[node.id], (draft) => {
          draft.inputFieldDefinitions = mergeFieldDefinitions(
            draft.inputFieldDefinitions,
            buildFieldDefinitionsFromSchemaFields(updatedSchema.fields)
          );
          draft.newProcessTaskDefinitionVersion.taskSchema.fields = updatedSchema.fields;
          if (updatedSchema.outputSchema != null) {
            draft.outputFieldDefinitions = mergeFieldDefinitions(
              draft.outputFieldDefinitions,
              buildFieldDefinitionsFromSchemaFields(updatedSchema.outputSchema.fields)
            );
            draft.newProcessTaskDefinitionVersion.taskSchema.outputSchema!.fields =
              updatedSchema.outputSchema.fields;
          }
        }),
      shouldIncrementNodeNames,
      editorMode
    );

    if (result.nodeChanges != null) {
      allNodeChanges.push(...result.nodeChanges);
    }
  }

  return allNodeChanges;
}
